/**
 * useDeployToken — wraps clanker-sdk deployToken with transaction state machine
 *
 * ARCH-4: SDK-direct pattern — deploy is client-side via clanker-sdk + wallet
 * ARCH-25: Transaction lifecycle state machine
 * ARCH-27: Contract interaction hook pattern
 *
 * Story 2.5 Task 4, Story 2.8 Task 1-2
 */

import type {
	AirdropMerkleData,
	CreateTokenDetailsFormData,
	SdkPresaleConfig,
	V4Config,
} from "@repo/shared-types";
import { transformV4ConfigForSdk } from "@repo/shared-types";
import { useCallback, useMemo, useRef, useState } from "react";
import { UserRejectedRequestError } from "viem";
import { getPublicClient, getWalletClient } from "wagmi/actions";

import { mapChainError } from "../../lib/chain-errors";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { useRegisterToken } from "../api/use-register-token";
import { useWallet } from "../use-wallet";
import type { TransactionStateActions } from "./use-transaction-state";
import { useTransactionState } from "./use-transaction-state";

export type DeployTokenParams = {
	details: Partial<CreateTokenDetailsFormData>;
	v4Config: Partial<V4Config>;
	logoUploadId?: string;
	airdropMerkleData?: AirdropMerkleData | undefined;
	/** Opaque SDK MerkleTree instance for post-deploy airdrop registration */
	merkleTree?: unknown | undefined;
};

export type DeployTokenResult = {
	contractAddress: `0x${string}` | undefined;
	poolAddress: `0x${string}` | undefined;
	tokenId: string | undefined;
	airdropRegistered: boolean;
	presaleStarted: boolean;
};

export type UseDeployTokenReturn = TransactionStateActions & {
	deploy: (params: DeployTokenParams) => Promise<void>;
	result: DeployTokenResult;
};

function isUserRejection(error: unknown): boolean {
	if (error instanceof UserRejectedRequestError) return true;
	if (!(error instanceof Error)) return false;
	const msg = error.message.toLowerCase();
	return msg.includes("user rejected") || msg.includes("user denied") || msg.includes("cancelled");
}

function isHexAddress(value: string): value is `0x${string}` {
	return /^0x[a-fA-F0-9]{40}$/.test(value);
}

const EMPTY_RESULT: DeployTokenResult = {
	contractAddress: undefined,
	poolAddress: undefined,
	tokenId: undefined,
	airdropRegistered: false,
	presaleStarted: false,
};

/**
 * Start presale after token deploy. Triggers a second wallet signature.
 * Returns true on success, false on failure (should not block deploy confirmation).
 */
async function startPresaleAfterDeploy(
	clanker: unknown,
	tokenConfig: unknown,
	presaleConfig: SdkPresaleConfig,
	creatorAddress: `0x${string}`
): Promise<boolean> {
	try {
		const { startPresale } = await import("@repo/clanker-sdk/v4/extensions");
		const presaleData = {
			clanker,
			tokenConfig,
			presaleConfig: { ...presaleConfig, recipient: creatorAddress },
		};
		// @ts-expect-error -- clanker (Clanker) and tokenConfig (ClankerTokenV4) are opaque SDK types at this boundary; SDK validates internally
		const presaleResult = await startPresale(presaleData);
		return !presaleResult.error;
	} catch (error: unknown) {
		// Presale start failure should not fail the deploy
		console.warn(
			"[useDeployToken] Presale start failed:",
			error instanceof Error ? error.message : error
		);
		return false;
	}
}

/**
 * Hook for deploying a token via the clanker SDK.
 *
 * Implements the full deploy lifecycle:
 * preparing → awaiting_signature → pending → confirmed | failed | rejected
 */
export function useDeployToken(): UseDeployTokenReturn {
	const txState = useTransactionState();
	const { address, getAccessToken } = useWallet();
	const { setPreparing, setAwaitingSignature, setPending, setConfirmed, setFailed, setRejected } =
		txState;
	const registerTokenMutation = useRegisterToken();
	const registerTokenRef = useRef(registerTokenMutation);
	registerTokenRef.current = registerTokenMutation;
	const [result, setResult] = useState<DeployTokenResult>(EMPTY_RESULT);

	const deploy = useCallback(
		async (params: DeployTokenParams) => {
			try {
				setPreparing();
				setResult(EMPTY_RESULT);

				const { details, v4Config } = params;
				if (!details.name || !details.symbol || !details.category) {
					setFailed("Missing required token details.");
					return;
				}

				if (!address || !isHexAddress(address)) {
					setFailed("Wallet not connected. Please connect your wallet.");
					return;
				}

				// Fetch wallet client and public client imperatively to avoid stale hook closures
				const activeChain = getActiveChain();
				const walletClient = await getWalletClient(wagmiConfig, {
					chainId: activeChain.id,
				}).catch(() => null);
				if (!walletClient) {
					setFailed("Wallet client not available. Please reconnect your wallet.");
					return;
				}

				const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });
				if (!publicClient) {
					setFailed("Network connection unavailable. Please check your connection.");
					return;
				}

				// Build SDK extensions from UI config
				const fullV4Config: V4Config = {
					feeSplitCreator: v4Config.feeSplitCreator ?? 50,
					feeSplitTreasury: v4Config.feeSplitTreasury ?? 30,
					feeSplitProtocol: v4Config.feeSplitProtocol ?? 20,
					extensions: v4Config.extensions ?? [],
				};
				const sdkExtensions = transformV4ConfigForSdk(fullV4Config, params.airdropMerkleData);

				// Build ClankerTokenV4 config for the SDK
				const { Clanker } = await import("@repo/clanker-sdk/v4");
				const clanker = new Clanker({
					wallet: walletClient,
					publicClient,
				});

				const tokenConfig = {
					name: details.name,
					symbol: details.symbol,
					image: "",
					chainId: activeChain.id,
					tokenAdmin: address,
					metadata: {
						description: details.description ?? "",
					},
					context: { interface: "0g-vibe-launchpad" as const },
					...(sdkExtensions.vault ? { vault: sdkExtensions.vault } : {}),
					...(sdkExtensions.airdrop ? { airdrop: sdkExtensions.airdrop } : {}),
					...(sdkExtensions.devBuy ? { devBuy: sdkExtensions.devBuy } : {}),
					...(sdkExtensions.presale
						? { presale: { bps: sdkExtensions.presale.presaleSupplyBps } }
						: {}),
				};

				setAwaitingSignature();

				// Deploy via SDK — this triggers the wallet popup
				// @ts-expect-error -- activeChain.id includes 0G testnet (16600) not yet in SDK's supported chain literal union; Zod validates at runtime
				const deployResult = await clanker.deploy(tokenConfig);

				if (deployResult.error) {
					setFailed(mapChainError(deployResult.error));
					return;
				}

				setPending(deployResult.txHash);

				// Wait for transaction receipt and extract contract address
				const receiptResult = await deployResult.waitForTransaction();

				if (receiptResult.error) {
					setFailed(mapChainError(receiptResult.error));
					return;
				}

				const contractAddress = receiptResult.address;

				// Extract pool/hooks address from the ClankerLocker contract.
				// The hooks address is the poolAddress needed for swap quotes.
				let poolAddress: `0x${string}` | undefined;
				try {
					const tokenRewards = await clanker.getTokenRewards({ token: contractAddress });
					const hooks = tokenRewards.poolKey?.hooks;
					if (hooks && isHexAddress(hooks)) {
						poolAddress = hooks;
					}
				} catch (error: unknown) {
					// Non-fatal: pool address can be backfilled by indexing
					console.warn(
						"[useDeployToken] Failed to read pool address from locker:",
						error instanceof Error ? error.message : error
					);
				}

				// Register token metadata via API (POST /v1/tokens)
				let tokenId: string | undefined;
				try {
					const accessToken = await getAccessToken();
					if (accessToken) {
						const activeChain = getActiveChain();
						const registration = await registerTokenRef.current.mutateAsync({
							request: {
								name: details.name,
								symbol: details.symbol,
								description: details.description,
								tagline: details.tagline,
								category: details.category,
								logoUploadId: params.logoUploadId,
								contractAddress: contractAddress,
								poolAddress,
								txHash: deployResult.txHash,
								chainId: activeChain.id,
								feeSplitCreator: fullV4Config.feeSplitCreator,
								feeSplitTreasury: fullV4Config.feeSplitTreasury,
								feeSplitProtocol: fullV4Config.feeSplitProtocol,
								extensions: JSON.stringify(fullV4Config.extensions),
							},
							token: accessToken,
							walletAddress: address,
						});
						tokenId = registration.id;
					}
				} catch (error: unknown) {
					// Metadata registration failure should not block the deploy success
					console.warn(
						"[useDeployToken] Token metadata registration failed:",
						error instanceof Error ? error.message : error
					);
				}

				// Post-deploy airdrop registration (fire-and-forget)
				let airdropRegistered = false;
				if (params.merkleTree && contractAddress) {
					try {
						const { registerAirdrop } = await import("@repo/clanker-sdk/v4/extensions");
						// @ts-expect-error -- MerkleTree type from SDK is opaque; tree was generated by SDK's createAirdrop
						const success = await registerAirdrop(contractAddress, params.merkleTree);
						airdropRegistered = Boolean(success);
					} catch (error: unknown) {
						// Airdrop registration failure should not fail the deploy
						console.warn(
							"[useDeployToken] Airdrop registration failed:",
							error instanceof Error ? error.message : error
						);
						airdropRegistered = false;
					}
				}

				// Post-deploy presale start (requires second wallet signature)
				let presaleStarted = false;
				if (sdkExtensions.presale && contractAddress) {
					presaleStarted = await startPresaleAfterDeploy(
						clanker,
						tokenConfig,
						sdkExtensions.presale,
						address
					);
				}

				setResult({
					contractAddress,
					poolAddress,
					tokenId,
					airdropRegistered,
					presaleStarted,
				});
				setConfirmed();
			} catch (error: unknown) {
				if (isUserRejection(error)) {
					setRejected();
				} else {
					setFailed(mapChainError(error));
				}
			}
		},
		[
			setPreparing,
			setAwaitingSignature,
			setPending,
			setConfirmed,
			setFailed,
			setRejected,
			address,
			getAccessToken,
		]
	);

	return useMemo(
		() => ({
			...txState,
			deploy,
			result,
		}),
		[txState, deploy, result]
	);
}
